import React, { Component } from "react";
import ReactPlayer from "react-player";
import { parseTeaserConfig } from "./videoTeaserConfig";
import "./TeaserVideoPlayer.css";

const shuffle = (arr) => {
  const copy = arr.slice();
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const sanitizeSegmentsByDuration = (segments, durationSec) => {
  if (!Array.isArray(segments)) return [];
  const hasDuration = Number.isFinite(durationSec) && durationSec > 0;

  return segments
    .map((s) => {
      const start = Math.max(0, Number(s?.startSec) || 0);
      const endRaw = Number(s?.endSec);
      const end = Number.isFinite(endRaw) ? endRaw : start + 1;
      const safeEnd = end > start ? end : start + 1;
      const finalEnd = hasDuration ? Math.min(safeEnd, durationSec) : safeEnd;
      if (finalEnd <= start) return null;
      return { startSec: start, endSec: finalEnd };
    })
    .filter(Boolean);
};

export default class TeaserVideoPlayer extends Component {
  static defaultProps = {
    src: "",
    teaserConfigJson: null,
    controls: true,
    width: "100%",
    height: "100%",
    className: "",
    style: undefined,
    progressInterval: 200,
  };

  constructor(props) {
    super(props);
    this.playerRef = React.createRef();
    this._switchTimeout = null;
    this._isSwitching = false;

    this.state = {
      durationSec: null,
      currentCfg: parseTeaserConfig(props.teaserConfigJson),
      segments: [],
      playOrder: [],
      playOrderPos: 0,
      transitioning: false,
      ended: false,
    };
  }

  componentDidMount() {
    this.recomputePlan(this.props.teaserConfigJson, null);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.teaserConfigJson !== this.props.teaserConfigJson ||
      prevProps.src !== this.props.src
    ) {
      this.recomputePlan(this.props.teaserConfigJson, this.state.durationSec);
    }

    if (prevState.durationSec !== this.state.durationSec) {
      this.recomputePlan(this.props.teaserConfigJson, this.state.durationSec);
    }
  }

  componentWillUnmount() {
    if (this._switchTimeout) {
      clearTimeout(this._switchTimeout);
      this._switchTimeout = null;
    }
  }

  buildOrder = (cfg, segments) => {
    const n = segments.length;
    if (n === 0) return [];
    const base = Array.from({ length: n }, (_, i) => i);

    if (cfg?.advanced?.order === "random") {
      if (cfg?.advanced?.allowRepeat) {
        return Array.from({ length: n }, () => Math.floor(Math.random() * n));
      }
      return shuffle(base);
    }

    return base;
  };

  recomputePlan = (rawCfg, durationSec) => {
    const currentCfg = parseTeaserConfig(rawCfg);

    let segments = [];
    if (currentCfg.mode === "clip") {
      segments = sanitizeSegmentsByDuration([currentCfg.clip], durationSec);
    } else if (currentCfg.mode === "advanced") {
      segments = sanitizeSegmentsByDuration(currentCfg.advanced.segments, durationSec);
    }

    const playOrder = this.buildOrder(currentCfg, segments);

    this.setState(
      {
        currentCfg,
        segments,
        playOrder,
        playOrderPos: 0,
        ended: false,
      },
      () => {
        this.seekToCurrentStart();
      }
    );
  };

  currentSegment = () => {
    const { segments, playOrder, playOrderPos } = this.state;
    if (!segments.length || !playOrder.length) return null;
    const segIdx = playOrder[playOrderPos] ?? 0;
    return segments[segIdx] || null;
  };

  seekToCurrentStart = () => {
    const seg = this.currentSegment();
    if (!seg) return;
    try {
      this.playerRef.current?.seekTo(seg.startSec, "seconds");
    } catch {}
  };

  handleDuration = (durationSec) => {
    this.setState({ durationSec: Number.isFinite(durationSec) ? durationSec : null });
  };

  goNextSegment = () => {
    const { currentCfg, segments, playOrder, playOrderPos } = this.state;
    if (!segments.length || !playOrder.length || this._isSwitching) return;

    const activeSeg = this.currentSegment();

    const nextPos = playOrderPos + 1;
    if (nextPos < playOrder.length) {
      this.jumpToSegmentAtPos(nextPos);
      return;
    }

    if (currentCfg.mode === "clip") {
      this.pausePlaybackAt(activeSeg?.endSec);
      this.setState({ ended: true });
      return;
    }

    if (!currentCfg?.advanced?.loop) {
      this.pausePlaybackAt(activeSeg?.endSec);
      this.setState({ ended: true });
      return;
    }

    const newOrder = this.buildOrder(currentCfg, segments);
    this.setState({ playOrder: newOrder, playOrderPos: 0 }, () => {
      this.jumpToSegmentAtPos(0);
    });
  };

  pausePlaybackAt = (seconds) => {
    try {
      if (Number.isFinite(seconds)) {
        this.playerRef.current?.seekTo(seconds, "seconds");
      }
      const internal = this.playerRef.current?.getInternalPlayer?.();
      if (internal?.pause) {
        internal.pause();
      } else if (internal?.pauseVideo) {
        internal.pauseVideo();
      }
    } catch {}
  };

  jumpToSegmentAtPos = (pos) => {
    const { currentCfg } = this.state;
    const transitionType = currentCfg?.advanced?.transition?.type || "none";
    const transitionMs = Number(currentCfg?.advanced?.transition?.durationMs) || 0;

    this._isSwitching = true;

    const doSeek = () => {
      this.setState({ playOrderPos: pos, ended: false }, () => {
        this.seekToCurrentStart();
        this._isSwitching = false;
      });
    };

    if (transitionType === "fade" && transitionMs > 0) {
      this.setState({ transitioning: true }, () => {
        if (this._switchTimeout) clearTimeout(this._switchTimeout);
        this._switchTimeout = setTimeout(() => {
          doSeek();
          this.setState({ transitioning: false });
          this._switchTimeout = null;
        }, transitionMs);
      });
      return;
    }

    doSeek();
  };

  handleProgress = (progress) => {
    const seg = this.currentSegment();
    if (!seg || this.state.ended) return;

    const played = Number(progress?.playedSeconds) || 0;
    if (played >= seg.endSec - 0.06) {
      this.goNextSegment();
    }
  };

  render() {
    const { src, controls, width, height, className, style, progressInterval } = this.props;
    const { transitioning } = this.state;

    return (
      <div className={`teaser-player-wrap ${className || ""}`} style={style}>
        <ReactPlayer
          ref={this.playerRef}
          src={src}
          controls={controls}
          width={width}
          height={height}
          progressInterval={progressInterval}
          onDuration={this.handleDuration}
          onProgress={this.handleProgress}
        />
        {transitioning && <div className="teaser-player-fade" />}
      </div>
    );
  }
}
